import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  AttachmentIconComponent,
  ChatIconComponent,
  ContributorsIconComponent,
  InfoIconComponent,
  CloseIconComponent,
} from '@kuiper/icons';
import { RequestViewDetailsComponent } from '../request-view/request-view-details/request-view-details.component';
import { RequestDiscussionComponent } from '../request-discussion/request-discussion.component';
import { TAB_IDS } from './request-view-tabs.constants';
import { DiscussionComment } from '../request-discussion/request-discussion.model';
import { RequestDetailsResponse } from '../../../../common/models/request-details.model';
import { OffCanvasComponent } from '../../../../common/components/off-canvas/off-canvas.component';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { TabComponent } from '../../../../../ui-components/lib/tabs/tab/tab.component';
import { TabsComponent } from '../../../../../ui-components/lib/tabs/tabs.component';
import { RequestContributorsComponent } from '../request-contributors/request-contributors.component';
import { NgTemplateOutlet } from '@angular/common';
import { MainService } from '../../../../common/services/main/main.service';
import { RequestService } from '../../../../common/services/request/request.service';

@Component({
  selector: 'lib-request-view-actions',
  imports: [
    TranslocoPipe,
    AttachmentIconComponent,
    ChatIconComponent,
    InfoIconComponent,
    OffCanvasComponent,
    TabsComponent,
    TabComponent,
    RequestViewDetailsComponent,
    RequestDiscussionComponent,
    ContributorsIconComponent,
    RequestContributorsComponent,
    NgTemplateOutlet,
    CloseIconComponent,
  ],
  templateUrl: './request-view-actions.component.html',
  styleUrl: './request-view-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestViewActionsComponent {
  readonly TAB_IDS = TAB_IDS;

  private readonly mainService = inject(MainService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly requestService = inject(RequestService);
  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );

  showUnreadMessagesIndicator = input<boolean>(false);
  showSideDetails = input<boolean>(false);
  offCanvasRequestDetailsResponse = input<RequestDetailsResponse>();
  flowId = input<number>(0);
  newComment = input<DiscussionComment>();
  deletedCommentId = input<number>();
  activeTabIndex = input<number>(0);
  showContributorsTab = input<boolean>(true);
  toggleDiscussionSidebar = output<void>();
  toggleAttachmentsSidebar = output<void>();
  openDetailsSidebar = output<void>();
  closeDetailsSidebar = output<void>();
  tabChanged = output<number>();

  turnOffOutSideClick = signal<boolean>(false);

  tabs = computed(() => [
    ...(this.showContributorsTab()
      ? [
          {
            id: TAB_IDS.CONTRIBUTORS,
            title: 'request_screen_contributors_button',
          },
        ]
      : []),
    {
      id: TAB_IDS.DETAILS,
      title: 'request_screen_see_details_side_menu_title',
    },
    {
      id: TAB_IDS.DISCUSSION,
      title: 'request_screen_discussion_button',
    },
    {
      id: TAB_IDS.ATTACHMENTS,
      title: 'request_screen_attachments_button',
    },
  ]);

  offCanvasTitle = computed(
    () => this.tabs()[this.activeTabIndex()]?.title ?? ''
  );

  collapsedSidebar = effect(() => {
    const isSideDetailsOpen = this.showSideDetails();
    const isRequestHeaderUiEnhancementEnabled =
      this.isRequestHeaderUiEnhancementEnabled();

    this.mainService.isSidebarCollapsed.set(
      isSideDetailsOpen && isRequestHeaderUiEnhancementEnabled
    );
  });

  onTabChanged(visualTabIndex: number) {
    this.tabChanged.emit(visualTabIndex);
  }

  handleTurnOffOutSideClick(value: boolean) {
    this.turnOffOutSideClick.set(value);
  }
}
